import React, { useEffect, useState, useReducer } from 'react';
import styled from 'styled-components';
import { TABLET_WIDTH } from '~~elements/Const';
import Button from '~~elements/Button';
import Input from '~~elements/Input';
import Tooltip from '~~elements/Tooltip';
import toast from '~~elements/Toast';
import { UserResource } from '~~apis/resource';

const StyledDiv = styled.div`
  width: 100%;
  font-size: 0.8rem;
  padding: 0 0 2rem 0;

  .permission {
    &--top {
      padding: 2rem 0 0;
      font-size: 1.4rem;
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 2px #aaa solid;
    }
    &--item {
      padding: 0.2rem 0;
      display: grid;
      grid-template-columns: 3fr 2fr 1fr;
      align-items: center;
      border-bottom: 1px #ccc solid;
      > :last-child {
        text-align: end;
      }
      > div.separate-items {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
    }
  }

  .error-msg {
    font-size: 0.6rem;
    color: #ff0000;
  }

  @media (max-width: ${TABLET_WIDTH}) {
    align-items: center;

    .permission {
      &--item {
        grid-template-columns: 1fr;
        text-align: center;
        > :last-child {
          text-align: center;
        }
        > div.separate-items {
          flex-direction: column;
        }
      }
    }
  }
`;

const permissionWeights = ['ADMIN', 'EDITOR', 'VIEWER'];
const permissionMapping = {
  ADMIN: '管理者',
  EDITOR: '編輯者',
  VIEWER: '瀏覽者',
};

const newUserInitialState = {
  permission: '',
  query: '',
  errorMsg: '',
  name: '',
  submitable: false,
};
function newUserReducer(state, action) {
  switch (action.type) {
    case 'reset':
      return { ...newUserInitialState };
    case 'start':
      return { ...newUserInitialState, permission: action.payload };
    case 'updateQuery':
      return { ...newUserInitialState, permission: state.permission, query: action.payload };
    case 'notFound':
      return { ...state, errorMsg: '找不到用戶，請重新搜尋', name: '', submitable: false };
    case 'found':
      return { ...state, errorMsg: '', ...action.payload, submitable: true };
    default:
      throw new Error();
  }
}

const initEditPermissionInfo = { permission: '', index: -1, newPermission: '' };
const initDeletePermissionInfo = { permission: '', index: -1 };

const PermissionList = ({ userInfo, target, updatePermission, reload, permissionTooltipMapping }) => {
  const [selfPermission, setSelfPermission] = useState('VIEWER');

  const [newUserState, newUserDispatch] = useReducer(newUserReducer, newUserInitialState);
  const [confirmEditInfo, setConfirmEditInfo] = useState(initEditPermissionInfo);
  const [confirmRemoveInfo, setConfirmRemoveInfo] = useState(initDeletePermissionInfo);

  useEffect(() => {
    const isAdmin = target.admins.find((admin) => admin.uniqid === userInfo.uniqid);
    if (isAdmin) {
      return setSelfPermission('ADMIN');
    }

    const isEditor = target.editors.find((editor) => editor.uniqid === userInfo.uniqid);
    if (isEditor) {
      return setSelfPermission('EDITOR');
    }

    return setSelfPermission('VIEWER');
  }, [target, userInfo]);

  const listItem = (users, permission) => {
    const editable =
      selfPermission !== 'VIEWER' &&
      permissionWeights.findIndex((p) => p === permission) >= permissionWeights.findIndex((p) => p === selfPermission);
    const lastAdmin = permission === 'ADMIN' && users.length === 1;

    const handleNewUserPermission = () => {
      newUserDispatch({ type: 'start', payload: permission });
    };

    const cancelNewUser = () => {
      newUserDispatch({ type: 'reset' });
    };

    const handleNewUserInput = (e) => {
      newUserDispatch({ type: 'updateQuery', payload: e.target.value });
    };

    const handleNewUserCheck = () => {
      let query = { email: newUserState.query };
      if (!newUserState.query.match(/@/)) {
        query = { userName: newUserState.query };
      }
      UserResource.exists(query).then(({ data }) => {
        if (!data.exists) {
          return newUserDispatch({ type: 'notFound' });
        }
        return newUserDispatch({
          type: 'found',
          payload: { name: `${data.first_name} ${data.last_name}`, uniqid: data.uniqid },
        });
      });
    };

    const handleNewUserSubmit = () => {
      updatePermission({
        userUniqid: newUserState.uniqid,
        targetUniqid: target.info.uniqid,
        authority: permission,
      })
        .then(() => {
          toast('新增成功！', { status: 'success', second: 1 });
          setTimeout(() => reload(), 1000);
        })
        .catch(() => {
          toast('發生錯誤，請稍後再試', { status: 'error', second: 2 });
        });
    };

    const handleEdit = (index) => () => {
      setConfirmEditInfo({ permission, index, newPermission: permission });
    };

    const cancelEdit = () => {
      setConfirmEditInfo(initEditPermissionInfo);
    };

    const handleEditInput = (e) => {
      setConfirmEditInfo({ ...confirmEditInfo, newPermission: e.target.value });
    };

    const confirmEdit = () => {
      const user = target[`${permission.toLowerCase()}s`][confirmEditInfo.index];
      if (!user) {
        return;
      }

      updatePermission({
        userUniqid: user.uniqid,
        targetUniqid: target.info.uniqid,
        authority: confirmEditInfo.newPermission,
      })
        .then(() => {
          toast('更新成功！', { status: 'success', second: 1 });
          setTimeout(() => reload(), 1000);
        })
        .catch(() => {
          toast('發生錯誤，請稍後再試', { status: 'error', second: 2 });
        });
    };

    const handleRemove = (index) => () => {
      setConfirmRemoveInfo({ permission, index });
    };

    const cancelRemove = () => {
      setConfirmRemoveInfo(initDeletePermissionInfo);
    };

    const confirmRemove = () => {
      const user = target[`${permission.toLowerCase()}s`][confirmRemoveInfo.index];
      if (!user) {
        return;
      }

      updatePermission({
        userUniqid: user.uniqid,
        targetUniqid: target.info.uniqid,
        authority: 'REMOVE',
      })
        .then(() => {
          toast('更新成功！', { status: 'success', second: 1 });
          setTimeout(() => reload(), 1000);
        })
        .catch(() => {
          toast('發生錯誤，請稍後再試', { status: 'error', second: 2 });
        });
    };

    return (
      <>
        <div className="permission--top">
          <div>
            {permissionMapping[permission]}
            <Tooltip content={permissionTooltipMapping[permission]} />
          </div>
          {editable ? (
            <Button mini width="unset" nowrap color="transparent" onClick={handleNewUserPermission}>
              新增
            </Button>
          ) : (
            <div></div>
          )}
        </div>
        {newUserState.permission === permission && (
          <div key="new-user" className="permission--item">
            <Input
              placeholder="輸入 email 或 用戶名稱"
              value={newUserState.query}
              onChange={handleNewUserInput}
              textAlign="left"
            />
            <div>
              {newUserState.submitable ? (
                <>{newUserState.name}</>
              ) : (
                <Button mini width="unset" nowrap color="transparent" onClick={handleNewUserCheck}>
                  檢查
                </Button>
              )}
            </div>
            {newUserState.submitable ? (
              <div>
                <Button mini width="unset" nowrap onClick={handleNewUserSubmit}>
                  確認新增
                </Button>
                <Button mini width="unset" nowrap color="transparent" onClick={cancelNewUser}>
                  取消
                </Button>
              </div>
            ) : (
              <div className="error-msg">
                {newUserState.errorMsg && <div>{newUserState.errorMsg}</div>}
                <Button mini width="unset" nowrap color="transparent" onClick={cancelNewUser}>
                  取消
                </Button>
              </div>
            )}
          </div>
        )}
        {users.map((user, index) => (
          <div key={user.uniqid} className="permission--item">
            <div>{user.email}</div>
            {confirmEditInfo.permission === permission && confirmEditInfo.index === index ? (
              <div>
                <select value={confirmEditInfo.newPermission} onChange={handleEditInput}>
                  {selfPermission === 'ADMIN' && <option value="ADMIN">{permissionMapping['ADMIN']}</option>}
                  <option value="EDITOR">{permissionMapping['EDITOR']}</option>
                  <option value="VIEWER">{permissionMapping['VIEWER']}</option>
                </select>
              </div>
            ) : (
              <div className="separate-items">
                <label>{`${user.first_name} ${user.last_name}`}</label>
                <label>{`@${user.user_name}`}</label>
              </div>
            )}
            <div>
              {editable && !lastAdmin ? (
                confirmRemoveInfo.permission === permission && confirmRemoveInfo.index === index ? (
                  <>
                    <Button mini width="unset" nowrap color="red" onClick={confirmRemove}>
                      確定刪除
                    </Button>
                    <Button mini width="unset" nowrap color="transparent" onClick={cancelRemove}>
                      取消
                    </Button>
                  </>
                ) : confirmEditInfo.permission === permission && confirmEditInfo.index === index ? (
                  <>
                    <Button mini width="unset" nowrap onClick={confirmEdit}>
                      更新
                    </Button>
                    <Button mini width="unset" nowrap color="transparent" onClick={cancelEdit}>
                      取消
                    </Button>
                  </>
                ) : (
                  <>
                    <Button mini width="unset" nowrap color="blue" onClick={handleEdit(index)}>
                      編輯
                    </Button>
                    <Button mini width="unset" nowrap color="transparent" onClick={handleRemove(index)}>
                      移除
                    </Button>
                  </>
                )
              ) : (
                <Button mini width="unset" nowrap color="transparent" disabled>
                  -
                </Button>
              )}
            </div>
          </div>
        ))}
      </>
    );
  };

  return (
    <StyledDiv>
      {listItem(target.admins, 'ADMIN')}
      {listItem(target.editors, 'EDITOR')}
      {/* {listItem(target.viewers, 'VIEWER')} */}
    </StyledDiv>
  );
};

export default PermissionList;
